import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import type { AddItemRequest, UpdateItemRequest } from '@/api/types'
import { menusApi } from '@/api/endpoints/menus'
import { getErrorMessage } from '@/shared/utils/error-codes'
import { ApiError } from '@/api/types'

/**
 * Mutation hook to add an item to a section
 */
export function useAddItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      sectionId,
      data,
    }: {
      menuId: number
      sectionId: number
      data: AddItemRequest
    }) => menusApi.addItem(menuId, sectionId, data),
    onSuccess: (result, variables) => {
      // Build toast message with warnings
      let message = 'Item added successfully'
      if (result.autoAssigned) {
        message += ' (auto-assigned to restaurant)'
      }
      if (result.priceMissing) {
        toast.warning(
          `${message}. Warning: Product has no price set for this restaurant.`,
        )
      } else {
        toast.success(message)
      }

      // Invalidate menu contents to refresh
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to add item')
      }
    },
  })
}

/**
 * Mutation hook to update an item
 */
export function useUpdateItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      sectionId,
      productId,
      data,
    }: {
      menuId: number
      sectionId: number
      productId: number
      data: UpdateItemRequest
    }) => menusApi.updateItem(menuId, sectionId, productId, data),
    onSuccess: (_result, variables) => {
      // Only show toast for explicit user actions (not sortOrder updates)
      if (
        variables.data.overrideName !== undefined ||
        variables.data.active !== undefined
      ) {
        toast.success('Item updated successfully')
      }

      // Invalidate menu contents to refresh
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to update item')
      }
    },
  })
}

/**
 * Moves an item into another section of the same menu
 * (CHG-2026-09-menu-editor-redesign). The item keeps its override name and its active flag.
 */
export function useMoveItem() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('menus')

  return useMutation({
    mutationFn: ({
      menuId,
      sectionId,
      productId,
      toSectionId,
      sortOrder,
    }: {
      menuId: number
      sectionId: number
      productId: number
      toSectionId: number
      sortOrder?: number
    }) =>
      menusApi.moveItem(menuId, sectionId, productId, toSectionId, sortOrder),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      // The one case a manager will actually hit: the dish is already filed there. Saying so is
      // the point — the drop silently doing nothing is what this change is fixing.
      if (
        error instanceof ApiError &&
        error.code === 'already_in_target_section'
      ) {
        toast.error(t('itemRow.moveDuplicate'))
        return
      }
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(t('itemRow.moveFailed'))
      }
    },
  })
}

/**
 * Mutation hook to delete an item
 */
export function useDeleteItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      sectionId,
      productId,
    }: {
      menuId: number
      sectionId: number
      productId: number
    }) => menusApi.deleteItem(menuId, sectionId, productId),
    onSuccess: (_result, variables) => {
      toast.success('Item removed successfully')
      // Invalidate menu contents to refresh
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to remove item')
      }
    },
  })
}

/**
 * Mutation hook to bulk add items by category
 */
export function useBulkAddItems() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      menuId,
      sectionId,
      categoryCode,
    }: {
      menuId: number
      sectionId: number
      categoryCode: string
    }) => menusApi.bulkAddItemsByCategory(menuId, sectionId, categoryCode),
    onSuccess: (result, variables) => {
      // Build detailed toast message
      let message = `Added ${result.added} items`
      const warnings: Array<string> = []

      if (result.autoAssignedCount > 0) {
        warnings.push(`${result.autoAssignedCount} auto-assigned`)
      }
      if (result.missingPriceCount > 0) {
        warnings.push(`${result.missingPriceCount} missing prices`)
      }

      if (warnings.length > 0) {
        message += ` (${warnings.join(', ')})`
      }

      if (result.missingPriceCount > 0) {
        toast.warning(message)
      } else {
        toast.success(message)
      }

      // Invalidate menu contents to refresh
      queryClient.invalidateQueries({
        queryKey: ['menus', variables.menuId, 'contents'],
      })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error('Failed to bulk add items')
      }
    },
  })
}
